"use client";

import * as React from "react";
import { LoaderCircle, RefreshCw } from "lucide-react";
import type { Job, StatusSefaz } from "@/types";
import { useEmpresa, useSession } from "@/core/session";
import { openScreen } from "@/core/tabs";
import { useApiQuery } from "@/lib/query";
import { fmt } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useNow } from "@/lib/use-now";
import { Dot } from "@/components/status";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/overlay";
import { APP_VERSION } from "@/features/auth/components";
import { useUi } from "./ui-store";

const item =
  "inline-flex h-full shrink-0 items-center gap-1.5 px-2.5 outline-none hover:bg-row-hover hover:text-fg focus-visible:bg-row-hover data-[state=open]:bg-row-hover";

function Sep() {
  return <span aria-hidden className="h-3.5 w-px shrink-0 bg-border" />;
}

export function StatusBar() {
  const empresa = useEmpresa();
  const usuario = useSession((s) => s.usuario);
  const now = useNow(60_000);
  const { data: sefaz, refetch: refetchSefaz, isFetching: isFetchingSefaz } = useApiQuery<StatusSefaz[]>("status-sefaz", "/status/sefaz", { refetchInterval: 60_000 });
  const { data: fila } = useApiQuery<Job[]>("status-fila", "/status/fila", { refetchInterval: (q) => ((q.state.data?.length ?? 0) > 0 ? 1500 : 6000) });
  if (!empresa) return null;

  const uf = empresa.endereco.uf;
  const minhaUf = sefaz?.find((s) => s.uf === uf);
  const pior = (s?: StatusSefaz) => (!s ? "neutral" : s.servicos.some((x) => x.status === "fora") ? "danger" : s.servicos.some((x) => x.status === "lento") ? "warning" : "success");
  const dias = empresa.certificado ? Math.floor((new Date(empresa.certificado.validade).getTime() - now) / 86_400_000) : null;
  const certTom = dias === null ? "danger" : dias < 7 ? "danger" : dias < 30 ? "warning" : "success";
  const nFila = fila?.length ?? 0;

  return (
    <footer data-print-hide className="flex h-[26px] shrink-0 items-center overflow-hidden border-t border-border bg-surface-2 text-xs text-muted">
      <button type="button" className={cn(item, "tnum")} onClick={() => openScreen("empresa")}>
        {fmt.cnpj(empresa.cnpj)}
      </button>
      <Sep />
      <button type="button" className={cn(item, "font-semibold tracking-wide", empresa.ambiente === "homologacao" ? "bg-warning-soft text-warning hover:bg-warning-soft hover:text-warning" : "")} onClick={() => openScreen("ambiente")}>
        <Dot tom={empresa.ambiente === "homologacao" ? "warning" : "success"} />
        {empresa.ambiente === "homologacao" ? "HOMOLOGAÇÃO" : "PRODUÇÃO"}
      </button>
      <Sep />
      <Popover>
        <PopoverTrigger className={item}>
          SEFAZ {uf}
          <Dot tom={pior(minhaUf)} />
        </PopoverTrigger>
        <PopoverContent side="top" align="start" className="w-[430px] p-0 shadow-lg border-border">
          <div className="flex h-8 items-center justify-between border-b border-border px-3 text-xs font-semibold">
            <span className="flex items-center gap-2">
              <span>Status SEFAZ</span>
              <button
                type="button"
                title="Atualizar medição SEFAZ"
                onClick={() => refetchSefaz()}
                className="text-muted hover:text-fg outline-none p-0.5 rounded"
              >
                <RefreshCw className={cn("size-3", isFetchingSefaz && "animate-spin")} />
              </button>
            </span>
            <span className="flex items-center gap-3 font-normal text-muted">
              <span className="flex items-center gap-1">
                <Dot tom="success" />
                Normal
              </span>
              <span className="flex items-center gap-1">
                <Dot tom="warning" />
                Lento
              </span>
              <span className="flex items-center gap-1">
                <Dot tom="danger" />
                Fora
              </span>
            </span>
          </div>
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-surface-2 text-muted">
                <tr>
                  <th className="h-7 px-3 text-left font-medium">UF</th>
                  <th className="h-7 px-2 text-left font-medium">CT-e</th>
                  <th className="h-7 px-2 text-left font-medium">MDF-e</th>
                  <th className="h-7 px-2 text-left font-medium">Eventos</th>
                  <th className="h-7 px-2 text-left font-medium">Consulta</th>
                </tr>
              </thead>
              <tbody>
                {(sefaz ?? [])
                  .slice()
                  .sort((a, b) => (a.uf === uf ? -1 : b.uf === uf ? 1 : a.uf.localeCompare(b.uf)))
                  .map((s) => (
                    <tr key={s.uf} className={cn("border-t border-border hover:bg-row-hover", s.uf === uf && "bg-primary-soft")}>
                      <td className="h-6 px-3 font-medium">
                        {s.uf}
                        {s.uf === uf ? <span className="ml-1 text-2xs text-primary font-normal">(sua)</span> : null}
                      </td>
                      {s.servicos.map((x) => (
                        <td key={x.nome} className="px-2">
                          <span className="inline-flex items-center gap-1.5 tnum">
                            <Dot tom={x.status === "ok" || (x.status as string) === "normal" ? "success" : x.status === "lento" ? "warning" : "danger"} />
                            {x.status === "fora" || x.ms === 0 ? "—" : `${x.ms} ms`}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-border bg-surface-2 px-3 py-1.5 text-2xs text-muted">
            <span>Telemetria oficial em tempo real</span>
            <button
              type="button"
              className="text-primary hover:underline font-medium"
              onClick={() => openScreen("sefaz-status")}
            >
              Ver painel completo →
            </button>
          </div>
        </PopoverContent>
      </Popover>
      <Sep />
      <button type="button" className={cn(item, certTom === "danger" && "text-danger", certTom === "warning" && "text-warning")} onClick={() => openScreen("certificado")}>
        Certificado {dias === null ? "ausente" : dias < 0 ? "vencido" : `${dias} dias`}
      </button>
      <Sep />
      <Popover>
        <PopoverTrigger className={cn(item, nFila > 0 && "text-info")}>
          Fila {nFila}
          {nFila > 0 ? <LoaderCircle className="size-3 animate-spin" /> : null}
        </PopoverTrigger>
        <PopoverContent side="top" align="start" className="w-80 p-0">
          <div className="flex h-8 items-center border-b border-border px-3 text-xs font-semibold">Fila de processamento</div>
          {nFila === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-muted">Fila vazia</p>
          ) : (
            <ul className="py-1">
              {fila!.map((j) => (
                <li key={j.id}>
                  <button type="button" className="flex w-full flex-col gap-1 px-3 py-1.5 text-left hover:bg-row-hover" onClick={() => openScreen("viagem", { id: j.id, numero: j.descricao.split(" ")[1] ?? "" })}>
                    <span className="flex w-full items-center gap-2 text-xs">
                      <LoaderCircle className="size-3 animate-spin text-info" />
                      <span className="flex-1 truncate">{j.descricao}</span>
                      <span className="text-subtle tnum">{j.progresso}%</span>
                    </span>
                    <span className="h-1 w-full overflow-hidden rounded-full bg-neutral-soft">
                      <span className="block h-full bg-info" style={{ width: `${Math.max(6, j.progresso)}%` }} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </PopoverContent>
      </Popover>

      <span className="ml-auto" />
      <button type="button" className={item} onClick={() => openScreen("perfil")}>
        {usuario?.nome}
      </button>
      <Sep />
      <button type="button" className={cn(item, "text-subtle")} onClick={() => useUi.getState().setShortcuts(true)}>
        {APP_VERSION}
      </button>
    </footer>
  );
}
